import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Stack,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Typography,
  TableBody,
  Menu,
  RadioGroup,
  FormControlLabel,
  Radio,
  OutlinedInput,
  LinearProgress,
} from "@mui/material";
import { GreenSelectForm, GreenSwitch } from "../../../../../common/Greens";
import { ReactComponent as ArrowdownIcon } from "../../../../../../vendor/_new/icon/sites/arrow_down.svg";
import { ReactComponent as InfoIcon } from "../../../../../../vendor/_new/icon/sites/info.svg";
import { ReactComponent as FilterIcon } from "../../../../../../vendor/_new/icon/sites/filter.svg";
import { ReactComponent as SearchIcon } from "../../../../../../vendor/_new/icon/navbar/search.svg";
import { tableHeadSx, tableBodySx } from "../../../detection/WAFEvent";
import { formatDate } from "../../../../../../utils/format";
import colors from "../../../../../../utils/colors";
import StickySaveBar from "../../../../../common/StickySaveBar";
import { cloneItems, collectRuleEnabledDiff, enabledLabel, fieldChange } from "../../../../../common/stickySaveBarUtils";
import useAuth from "../../../../../../hooks/useAuth";
import useWAFConfig from "../../../../../../hooks/user/useWAFConfig";
import { CrsSecRuleId, FeatureId, UserType } from "../../../../../../utils/constants";
import { useLocation } from "react-router-dom";
import { useNotification } from "../../../../../../contexts/NotificationContext";
import axios from "../../../../../../utils/axios/v1/userAxios";

const withSecRuleId = (rules) =>
  (rules || []).map((rule) => ({ ...rule, id: rule.sec_rule_id }));

const ConfigWAFConfigTabContent = ({ siteUid, canConfigureWAFForSite }) => {
  const { isAuthenticated, userType, isFeatureEnabled } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { wafConfig, getSdSigRules, selectCurrentSdSigRule, sdSigRules, curSdSigRule, setErr } =
    useWAFConfig();
  const { state } = useLocation();

  const [ruleID, setRuleID] = useState();
  const [savedRuleGroupEnabled, setSavedRuleGroupEnabled] = useState(false);
  const [workingRuleGroupEnabled, setWorkingRuleGroupEnabled] = useState(false);
  const [savedCrssecrules, setSavedCrssecrules] = useState([]);
  const [workingCrssecrules, setWorkingCrssecrules] = useState([]);
  const [isSavingToggles, setIsSavingToggles] = useState(false);

  const [isInitialized, setIsInitialized] = useState(true);
  useEffect(() => {
    window.scrollTo(0, 0);
    if (isAuthenticated) {
      getSdSigRules().then(() => setIsInitialized(false));
    }
    setErr(null);
    return () => setErr(null);
  }, [isAuthenticated, getSdSigRules, setErr]);

  useEffect(() => {
    if (!ruleID) return;
    if (!siteUid) return;
    setIsInitialized(true);
    selectCurrentSdSigRule(ruleID, siteUid).then(() => setIsInitialized(false));
  }, [ruleID, selectCurrentSdSigRule, siteUid]);

  useEffect(() => {
    if (sdSigRules === null) return;
    if (sdSigRules.length === 0) return;
    if (CrsSecRuleId.MIN_SD_SIG <= state?.crs_sec_rule_id && CrsSecRuleId.MAX_SD_SIG >= state?.crs_sec_rule_id) {
      const nextRuleId = Math.floor(state?.crs_sec_rule_id / 1000).toString();
      setRuleID(nextRuleId);
    } else {
      setRuleID(sdSigRules[0]?.rule_id);
    }
  }, [sdSigRules, state?.crs_sec_rule_id]);

  useEffect(() => {
    if (!curSdSigRule) return;
    const rules = curSdSigRule?.crs_sec_rules || [];
    const groupEnabled = !!curSdSigRule.enabled;
    setSavedRuleGroupEnabled(groupEnabled);
    setWorkingRuleGroupEnabled(groupEnabled);
    setSavedCrssecrules(rules);
    setWorkingCrssecrules(cloneItems(rules));
  }, [curSdSigRule]);

  const stickyModifiedFields = useMemo(() => {
    const fields = [];
    if (savedRuleGroupEnabled !== workingRuleGroupEnabled) {
      fields.push(
        fieldChange(
          curSdSigRule?.description || curSdSigRule?.name || "Rule group",
          enabledLabel(savedRuleGroupEnabled),
          enabledLabel(workingRuleGroupEnabled),
        ),
      );
    }
    fields.push(
      ...collectRuleEnabledDiff(
        withSecRuleId(savedCrssecrules),
        withSecRuleId(workingCrssecrules),
        (rule) => rule.sec_rule_id,
      ),
    );
    return fields;
  }, [
    savedRuleGroupEnabled,
    workingRuleGroupEnabled,
    savedCrssecrules,
    workingCrssecrules,
    curSdSigRule?.description,
    curSdSigRule?.name,
  ]);
  const stickyModifiedCount = stickyModifiedFields.length;

  const canEditToggles = canConfigureWAFForSite && userType !== UserType.ORG_USER;

  const handleRuleGroupEnableChange = () => {
    if (!canEditToggles || isSavingToggles) return;
    setWorkingRuleGroupEnabled((prev) => !prev);
  };

  const handleSecRuleEnableChange = (secruleId, enabled) => {
    if (!canEditToggles || isSavingToggles || !workingRuleGroupEnabled) return;
    setWorkingCrssecrules((prev) =>
      (prev || []).map((secrule) => (secrule?.sec_rule_id === secruleId ? { ...secrule, enabled } : secrule)),
    );
  };

  const discardToggleChanges = useCallback(() => {
    setWorkingRuleGroupEnabled(savedRuleGroupEnabled);
    setWorkingCrssecrules(cloneItems(savedCrssecrules));
  }, [savedRuleGroupEnabled, savedCrssecrules]);

  const applyToggleChanges = useCallback(async () => {
    if (!canEditToggles || !siteUid || !curSdSigRule?.rule_id || stickyModifiedCount === 0) return;

    const tasks = [];
    if (savedRuleGroupEnabled !== workingRuleGroupEnabled) {
      tasks.push(() =>
        axios.patch(`/config/waf/${siteUid}/crs_rule/${curSdSigRule.rule_id}`, {
          enabled: workingRuleGroupEnabled,
        }),
      );
    }

    const savedBySecId = Object.fromEntries((savedCrssecrules || []).map((rule) => [rule.sec_rule_id, !!rule.enabled]));
    const changedRules = (workingCrssecrules || []).filter((rule) => savedBySecId[rule.sec_rule_id] !== !!rule.enabled);
    for (const rule of changedRules) {
      tasks.push(() =>
        axios.patch(`/config/waf/${siteUid}/crs_sec_rule/${rule.sec_rule_id}`, {
          rule_id: curSdSigRule.rule_id?.toString(),
          enabled: !!rule.enabled,
        }),
      );
    }

    if (tasks.length === 0) return;

    setIsSavingToggles(true);
    try {
      await Promise.all(tasks.map((task) => task()));
      const nextGroupEnabled = workingRuleGroupEnabled;
      const nextRules = cloneItems(workingCrssecrules);
      setSavedRuleGroupEnabled(nextGroupEnabled);
      setWorkingRuleGroupEnabled(nextGroupEnabled);
      setSavedCrssecrules(nextRules);
      setWorkingCrssecrules(nextRules);
      showSuccess(`Saved ${stickyModifiedCount} Layerd signature change${stickyModifiedCount === 1 ? "" : "s"}.`);
    } catch (err) {
      const cpMsg =
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string" ? err.response.data : null) ||
        err?.message ||
        "Failed to update Layerd signature rules";
      showError(cpMsg);
      await selectCurrentSdSigRule(curSdSigRule.rule_id, siteUid);
    } finally {
      setIsSavingToggles(false);
    }
  }, [
    canEditToggles,
    siteUid,
    curSdSigRule?.rule_id,
    stickyModifiedCount,
    savedRuleGroupEnabled,
    workingRuleGroupEnabled,
    savedCrssecrules,
    workingCrssecrules,
    selectCurrentSdSigRule,
    showError,
    showSuccess,
  ]);

  const [selectedStatus, setSelectedStatus] = useState("Show all");
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const statusMenuOpen = Boolean(statusAnchorEl);
  const handleStatusMenuOpen = (event) => setStatusAnchorEl(event.currentTarget);
  const handleStatusMenuClose = () => setStatusAnchorEl(null);

  const [selectedRuleId, setSelectedRuleId] = useState("");
  const [ruleIdAnchorEl, setRuleIdAnchorEl] = useState(null);
  const ruleIdMenuOpen = Boolean(ruleIdAnchorEl);
  const handleRuleIdMenuOpen = (event) => setRuleIdAnchorEl(event.currentTarget);
  const handleRuleIdMenuClose = () => setRuleIdAnchorEl(null);

  const filteredRules = workingCrssecrules
    ?.filter((rule) => {
      if (selectedStatus === "Enabled") return rule?.enabled;
      if (selectedStatus === "Disabled") return !rule?.enabled;
      return true;
    })
    ?.filter((rule) => rule?.sec_rule_id?.includes(selectedRuleId));

  return (
    <>
      {isInitialized && (
        <Box sx={{ width: "100%", position: "fixed", top: 66 }}>
          <LinearProgress
            sx={{
              "& .MuiLinearProgress-bar1Indeterminate": { backgroundColor: colors.primary[200] },
              "& .MuiLinearProgress-bar2Indeterminate": { backgroundColor: colors.primary[200] },
              height: 4,
            }}
          />
        </Box>
      )}
      <Stack spacing={6}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          spacing={4}
          sx={{
            p: 3,
            backgroundColor: `rgba(17, 17, 17, 0.04)`,
            borderRadius: "8px",
            width: "max-content",
          }}
        >
          <GreenSelectForm variant="outlined">
            <InputLabel id="sdrule-selector">Layerd Signature WAF Rules</InputLabel>
            <Select
              labelId="sdrule-selector"
              IconComponent={ArrowdownIcon}
              value={ruleID ?? ""}
              onChange={(e) => setRuleID(e.target.value)}
              sx={{ width: 400 }}
            >
              {sdSigRules?.map((rule, index) => {
                return (
                  <MenuItem
                    key={`rules_${index}`}
                    value={rule.rule_id ?? ""}
                    sx={{
                      height: 38,
                      fontSize: 14,
                      lineHeight: "21px",
                      "&:hover": { color: colors.primary[300] },
                    }}
                  >
                    {rule.description || rule.name}
                  </MenuItem>
                );
              })}
              {!isFeatureEnabled(FeatureId.CUSTOM_WAF_RULES) ? (
                <></>
              ) : (
                <MenuItem
                  value={"400"}
                  sx={{
                    height: 38,
                    fontSize: 14,
                    lineHeight: "21px",
                    "&:hover": { color: colors.primary[300] },
                  }}
                >
                  Custom Rules
                </MenuItem>
              )}
            </Select>
          </GreenSelectForm>
          <GreenSwitch
            disabled={!canEditToggles || isSavingToggles}
            checked={workingRuleGroupEnabled}
            onChange={handleRuleGroupEnableChange}
          />
          <Tooltip title={workingRuleGroupEnabled ? "Disable the rule group" : "Enable the rule group"}>
            <Box sx={{ width: 24, height: 24 }}>
              <InfoIcon style={{ color: colors.neutral[600] }} />
            </Box>
          </Tooltip>
        </Stack>
        <Box sx={{ width: "100%", borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}`, p: 2 }}>
          <Table
            sx={{
              "& .MuiTableCell-head": { py: 3, px: 3 },
              "& .MuiTableCell-body": { py: 2, px: 3 },
              "& .MuiTableCell-root": { borderBottom: "none" },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "20%" }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    onClick={(e) => {
                      setSelectedRuleId("");
                      handleRuleIdMenuOpen(e);
                    }}
                    sx={{ cursor: "pointer" }}
                  >
                    <Typography sx={{ ...tableHeadSx, color: ruleIdMenuOpen ? colors.primary[300] : "inherit" }}>Rule ID</Typography>
                    <Stack alignItems={"center"} justifyContent={"center"} sx={{ width: 24, height: 24 }}>
                      <FilterIcon style={{ color: colors.neutral[600] }} />
                    </Stack>
                  </Stack>
                </TableCell>
                <TableCell sx={{ width: "70%" }}>
                  <Typography sx={tableHeadSx}>Description</Typography>
                </TableCell>
                <TableCell sx={{ width: "10%" }}>
                  <Stack direction="row" alignItems="center" spacing={2} onClick={handleStatusMenuOpen} sx={{ cursor: "pointer" }}>
                    <Typography sx={{ ...tableHeadSx, color: statusMenuOpen ? colors.primary[300] : "inherit" }}>Status</Typography>
                    <Stack alignItems={"center"} justifyContent={"center"} sx={{ width: 24, height: 24 }}>
                      <FilterIcon style={{ color: colors.neutral[600] }} />
                    </Stack>
                  </Stack>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRules?.map((rule, index) => (
                <TableRow key={`rule_${index}`}>
                  <TableCell>
                    <Typography sx={tableBodySx}>{rule?.sec_rule_id}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableBodySx}>{rule?.description}</Typography>
                  </TableCell>
                  <TableCell>
                    <GreenSwitch
                      disabled={!workingRuleGroupEnabled || !canEditToggles || isSavingToggles}
                      checked={!!rule?.enabled}
                      onChange={(e) => handleSecRuleEnableChange(rule?.sec_rule_id, e.target.checked)}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {filteredRules?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography sx={tableBodySx} textAlign={"center"}>
                      No rules to show
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
        <Typography sx={tableBodySx}>Last updated {formatDate(wafConfig?.updated_at)}</Typography>
      </Stack>
      <Menu
        anchorEl={statusAnchorEl}
        open={statusMenuOpen}
        onClose={handleStatusMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          top: 13,
          left: -12,
          "& .MuiList-root": {
            backgroundColor: "white",
            borderRadius: "8px",
            border: `solid 1px ${colors.neutral[200]}`,
            width: 160,
            boxShadow: "none",
            py: 0,
          },
          "& .MuiMenu-paper": {
            boxShadow: "none",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <RadioGroup
            value={selectedStatus}
            onChange={(e) => {
              setSelectedStatus(e.target.value);
              handleStatusMenuClose();
            }}
          >
            {["Show all", "Enabled", "Disabled"].map((status, index) => (
              <FormControlLabel
                key={`status_${index}`}
                value={status}
                control={
                  <Radio
                    size="small"
                    sx={{
                      color: colors.neutral[300],
                      "&.Mui-checked": {
                        color: colors.secondary[300],
                      },
                    }}
                  />
                }
                label={status}
                sx={{
                  height: 37,
                  backgroundColor: selectedStatus === status ? colors.primary["alpha"] : "inherit",
                  borderRadius: "8px",
                  "& .MuiFormControlLabel-label": {
                    fontSize: 14,
                    lineHeight: "21px",
                    color: colors.neutral[800],
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 210,
                    "&:hover": { color: colors.primary[300] },
                  },
                }}
              />
            ))}
          </RadioGroup>
        </Box>
      </Menu>
      <Menu
        anchorEl={ruleIdAnchorEl}
        open={ruleIdMenuOpen}
        onClose={handleRuleIdMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          top: 13,
          left: -12,
          "& .MuiList-root": {
            backgroundColor: "white",
            borderRadius: "8px",
            border: `solid 1px ${colors.neutral[200]}`,
            width: 270,
            boxShadow: "none",
            py: 0,
          },
          "& .MuiMenu-paper": {
            boxShadow: "none",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <OutlinedInput
            startAdornment={<SearchIcon style={{ color: colors.neutral[600] }} />}
            placeholder="Search Rule ID"
            value={selectedRuleId}
            onChange={(e) => setSelectedRuleId(e.target.value)}
            sx={{
              width: 240,
              height: 32,
              borderRadius: "8px",
              fontSize: 14,
              lineHeight: "21px",
              borderColor: colors.neutral[600],
              pl: 2,
              "& .MuiOutlinedInput-input": {
                px: 2,
              },
              "& .MuiInputBase-input::placeholder": {
                fontSize: 14,
                lineHeight: "21px",
              },
            }}
          />
        </Box>
      </Menu>
      <StickySaveBar
        open={canEditToggles && stickyModifiedCount > 0}
        modifiedCount={stickyModifiedCount}
        modifiedFields={stickyModifiedFields}
        onDiscard={discardToggleChanges}
        onApply={applyToggleChanges}
        isApplying={isSavingToggles}
      />
    </>
  );
};
export default ConfigWAFConfigTabContent;
